import { useCallback } from 'react';
import {
  useUploadFile,
  useGetDownloadUrl,
  useDeleteFile,
} from '../api/services/lupp/fileUpload/fileUpload';
import type { FileUploadResponse } from '../api/services/lupp/fileUpload/dto.fileUpload';

type UseFileUploadOptions = {
  /**
   * Customer ID for the upload
   */
  customerId?: string;
  /**
   * File category (e.g., 'DRIVERS_LICENSE', 'REGISTRATION', 'ATTACHMENT')
   */
  category?: string;
  /**
   * Additional metadata tags
   */
  tags?: Record<string, string>;
  /**
   * Callback when upload URL is successfully obtained
   */
  onUploadUrlSuccess?: (response: FileUploadResponse, file: File) => void;
  /**
   * Callback when upload completes successfully (after S3 upload)
   */
  onSuccess?: (fileUrl: string, file: File, fileId: string) => void;
  /**
   * Callback when upload fails
   */
  onError?: (error: Error, file: File) => void;
};

/**
 * Hook for handling file uploads with S3 pre-signed URLs via FileHub API
 *
 * @param options - Configuration options
 * @returns Object with getUploadUrl function and loading states
 *
 * @example
 * ```tsx
 * const { getUploadUrl, isLoading } = useFileUpload({
 *   customerId: 'CUST12345',
 *   category: 'ATTACHMENT',
 *   onSuccess: (url, file, fileId) => console.log('Uploaded:', fileId),
 * });
 *
 * <FileUpload
 *   dataTestId="attachments"
 *   getUploadUrl={getUploadUrl}
 *   onUploadComplete={(file, url) => console.log('Done')}
 * />
 * ```
 */
export const useFileUpload = (options: UseFileUploadOptions = {}) => {
  const { customerId, category, tags, onUploadUrlSuccess, onSuccess, onError } = options;

  const uploadFileMutation = useUploadFile();

  const deleteFileMutation = useDeleteFile();

  /**
   * Get a presigned upload URL for a file
   * This is called by the FileUpload component before uploading to S3
   */
  const getUploadUrl = useCallback(
    async (file: File) => {
      const response = await uploadFileMutation.mutateAsync({
        fileNameOriginal: file.name,
        mimeType: file.type || 'application/octet-stream',
        sizeBytes: file.size,
        customerId: customerId ?? '',
        category: category ?? 'GENERAL',
        consentFlag: true,
        attributes: tags,
      });

      onUploadUrlSuccess?.(response, file);

      // Prepare dev response for display (exclude sensitive data like presignedUrl)
      const devResponse = {
        fileId: response.fileId,
        tenantId: response.tenantId,
        customerId: response.customerId,
        category: response.category,
        fileNameOriginal: response.fileNameOriginal,
        fileNameSanitized: response.fileNameSanitized,
        mimeType: response.mimeType,
        createdAt: response.createdAt,
      };

      return {
        uploadUrl: response.presignedUrl,
        fileUrl: response.fileId,
        fileId: response.fileId,
        tenantId: response.tenantId,
        devResponse,
      };
    },
    [customerId, category, tags, uploadFileMutation, onUploadUrlSuccess]
  );

  /**
   * Handle successful upload completion
   * Note: With the new FileHub API, S3 events automatically confirm uploads
   */
  const handleUploadComplete = useCallback(
    async (fileUrl: string, file: File, fileId?: string) => {
      try {
        // S3 event notifications handle status update to AVAILABLE automatically
        // Just notify the consumer
        onSuccess?.(fileUrl, file, fileId || '');
      } catch (error) {
        onError?.(error instanceof Error ? error : new Error('Upload completion failed'), file);
      }
    },
    [onSuccess, onError]
  );

  /**
   * Delete a file by ID
   * Returns the fileId on success
   */
  const deleteFile = useCallback(
    async (fileId: string, reason?: string): Promise<string> => {
      return deleteFileMutation.mutateAsync({ fileId, reason });
    },
    [deleteFileMutation]
  );

  /**
   * Handle file removal - matches onFileRemove callback signature
   * Use this directly with FileUpload's onFileRemove prop
   */
  const handleFileRemove = useCallback(
    (fileId?: string) => {
      if (fileId) {
        deleteFileMutation.mutate(
          { fileId, reason: 'User removed file from upload' },
          {
            onError: (error) => {
              console.error('Failed to delete file:', error);
            },
          }
        );
      }
    },
    [deleteFileMutation]
  );

  return {
    getUploadUrl,
    handleUploadComplete,
    deleteFile,
    handleFileRemove,
    isLoading: uploadFileMutation.isPending,
    isError: uploadFileMutation.isError,
    error: uploadFileMutation.error,
    isDeleting: deleteFileMutation.isPending,
    deleteError: deleteFileMutation.error,
  };
};

/**
 * Hook for getting a download URL for a file
 */
export const useFileDownload = (fileId: string, enabled = true) => {
  return useGetDownloadUrl({ fileId, enabled });
};
